import { useContext, useMemo, useEffect } from 'react';
import { useCartContext } from '../../../utils/cart/context/CartContext';
import { UseCouponContextProps, CartCoupon } from '../types';
import { CouponProviderContext } from '../CouponContextProvider';
import { Coupon } from '@jbhi-fi/components/src/types';
import { tryParseJson } from '@jbhi-fi/utils/src/JSON/tryParseJson';
import { ShopifyStorefrontClient } from '../../../api/shopify/ShopifyStorefrontClient';
import { updateCart, setCartLoading } from '../../../utils/cart/cartUtils';
import { ShopifyCustomerClient } from '../../../templates/components/customers/api/shopifyCustomer/ShopifyCustomerClient';
import { getFilteredCoupons, getApplicableCoupons, generateAddToWalletQRCode } from '../utils';
import { useQuery, useQueryClient } from 'react-query';
import { useCouponTracking } from '../../../hooks/useCouponTracking/useCouponTracking';
import { LocalStorageKeys, getLocal, setLocal } from '@jbhi-fi/utils/src/localStorage';
import { useSpendAndGetProgress } from './useSpendAndGetCouponProgress';
import { getSession, SessionStorageKeys, setSession } from '@jbhi-fi/utils/src/sessionStorage';
import { Location } from '../types';
import { useCouponExclusions } from './useCouponExclusions';
import { CouponWalletContent } from '@jbhi-fi/components/src/CouponModal/constants';
import { SubscriptionsClient } from '../../../templates/components/customers/api/subscriptions/SubscriptionsClient';

const storefrontClient = new ShopifyStorefrontClient();
const shopifyCustomerClient = new ShopifyCustomerClient();

export const useCouponContext = ({ location, product }: UseCouponContextProps) => {
  const context = useContext(CouponProviderContext);
  const { cart } = useCartContext();
  const queryClient = useQueryClient();

  if (!context) throw new Error('useCouponContext must be used within a CouponProvider');

  const {
    isCouponSurfacingEnabled,
    isCouponSurfacingEnabledForAttachSlideout,
    isCouponSurfacingEnabledForCart,
    coupons,
    isLoading,
    isModalOpen,
    selectedCoupon,
    isFetched,
    isActive,
    isCartPage,
    shouldShowSpendAndGetBanner,
    couponOffering,
    setCoupons,
    setSelectedCoupon,
    setIsModalOpen,
    setIsLoading,
    setShouldShowSpendAndGetBanner,
  } = context;

  const couponExternalIds = useMemo(() => {
    return [...new Set(coupons.map(c => c.couponExternalId?.toString()))];
  }, [coupons]);

  const { data: exclusionMap } = useCouponExclusions(couponExternalIds);

  const handleToggleIsModalOpen = () => {
    setIsModalOpen(!isModalOpen);
  };

  const { trackCouponEvent } = useCouponTracking(selectedCoupon);

  const handleSelectCoupon = (couponCode: string, locationOverride?: Location) => {
    const matchingCoupon = coupons.find(coupon => coupon.couponCode === couponCode);

    setIsModalOpen(true);
    if (matchingCoupon) {
      setSelectedCoupon(matchingCoupon);
    }
    setSession(SessionStorageKeys.CouponLocation, locationOverride ?? location, false);
    trackCouponEvent({ event: 'click_coupon_redeem', location: locationOverride ?? location, coupon: matchingCoupon });
    trackCouponEvent({ event: 'view_popup', location: locationOverride ?? location, coupon: matchingCoupon });
  };

  const handleDeselectCoupon = () => {
    setSelectedCoupon(null);
    setIsModalOpen(false);
  };

  const handleUpdateShowSpendAndGetBanner = (shouldShowSpendAndGetBanner: boolean) => {
    setLocal(LocalStorageKeys.ShouldShowSpendAndGetBanner, shouldShowSpendAndGetBanner);
    setShouldShowSpendAndGetBanner(shouldShowSpendAndGetBanner);

    if (!shouldShowSpendAndGetBanner && couponOffering) {
      trackCouponEvent({
        event: 'close_banner',
        coupon: couponOffering,
        message: couponOffering?.content.displayName,
        type: couponOffering?.type,
      });
    }
  };

  const handleApplyCoupon = async (shouldCloseModal = false, inputCouponCode = '') => {
    if (!selectedCoupon && !inputCouponCode) return;
    const lastestCouponLocation = getSession(SessionStorageKeys.CouponLocation) || location;
    if (inputCouponCode) {
      setCartLoading(false);
    } else {
      setIsLoading(false);
    }
    if (!inputCouponCode) {
      trackCouponEvent({
        event: 'click_coupon_cta',
        label: 'Apply coupon to My Cart',
        location: lastestCouponLocation,
      });
    }

    //TODO: Remove - this when we have a better way to handle this
    const isAppliedSpendAndGet = selectedCoupon?.couponReference.toLowerCase().includes('spendandget');

    if (selectedCoupon?.type === 'SpendAndGet' || isAppliedSpendAndGet) {
      if (!shouldShowSpendAndGetBanner) {
        handleUpdateShowSpendAndGetBanner(true);
      }
    }

    const couponCode = inputCouponCode || selectedCoupon?.couponCode || '';
    const { couponExternalId: externalId = '', type: couponType, minimumSpend } = selectedCoupon ?? {};
    const couponReference = inputCouponCode
      ? coupons.find(c => c.couponCode === inputCouponCode)?.couponReference || ''
      : selectedCoupon?.couponReference || '';

    const updatedCart = await storefrontClient.setCartAttributes(cart?.attributes ?? {}, {
      _coupons: JSON.stringify([
        ...(tryParseJson<Array<CartCoupon>>(cart?.attributes?._coupons || '') || []),
        {
          externalId,
          code: couponCode,
          coupon: couponReference,
          action: inputCouponCode ? 'manual' : 'digital',
          location: lastestCouponLocation,
          status: 'loading',
          minimumSpend: minimumSpend,
          type: couponType,
        },
      ]),
    });

    const { cartApiResponse } = await updateCart(updatedCart);
    const isCouponValid = !cartApiResponse?.UnusedCoupons?.some(unusedCoupon => unusedCoupon.Reference === couponCode);
    setCoupons(getFilteredCoupons(coupons, [{ code: couponCode, status: 'loading' }], isCartPage));
    const couponAdded = coupons?.find(c => c.couponCode === inputCouponCode);
    trackCouponEvent({
      event: 'coupon_used',
      location: lastestCouponLocation,
      couponStatusInCart: { is_valid: isCouponValid ? 'valid_coupon' : 'invalid_coupon' },
      ...(inputCouponCode
        ? {
            coupon: {
              ...(couponAdded as Coupon),
              couponName: inputCouponCode,
            },
            action: 'manual',
          }
        : {}),
    });

    if (inputCouponCode) {
      setCartLoading(false);
    } else {
      setIsLoading(false);
    }
    if (shouldCloseModal) {
      handleDeselectCoupon();
    }
  };
  const hasProduct = !!product && product.sku !== '0';

  const { data: includedCoupons = [] } = useQuery({
    enabled: isActive && hasProduct && !!exclusionMap,
    queryKey: ['getExclusions', couponExternalIds],
    refetchOnWindowFocus: false,
    queryFn: () => {
      if (!exclusionMap) return;
      return getApplicableCoupons(coupons, exclusionMap, product);
    },
    staleTime: 1000 * 60 * 15, // 15 minutes
  });

  const couponsWithExclusions = coupons.filter(coupon => includedCoupons.find(c => c.couponCode === coupon.couponCode));

  const handleViewCoupons = async () => {
    const shopifyCustomerId = window.Jbhifi?.shopifyCustomerId;
    if (!coupons.length) return;
    const simpleCoupons = coupons
      .filter(coupon => coupon.isViewed !== true)
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      .map(({ content, ...simpleCoupon }) => simpleCoupon);

    const result = await shopifyCustomerClient.updateCoupons({
      customerId: shopifyCustomerId,
      coupons: simpleCoupons,
    });

    if (result?.statusCode === 200) {
      const viewedCoupons = coupons.map(coupon => (coupon.isViewed ? coupon : { ...coupon, isViewed: true }));
      setCoupons(viewedCoupons);
    } else {
      console.error('Failed to update coupons', result);
    }
  };

  const handleRemoveCoupon = async (couponCode: string) => {
    const lastestCouponLocation = (getSession(SessionStorageKeys.CouponLocation) || location) as Location;
    const updatedCoupons = (tryParseJson<Array<CartCoupon>>(cart?.attributes?._coupons) || []).filter(
      coupon => coupon.code !== couponCode
    );
    const updatedCart = await storefrontClient.setCartAttributes(cart?.attributes ?? {}, {
      _coupons: JSON.stringify(updatedCoupons),
    });

    await updateCart(updatedCart);
    setCoupons(getFilteredCoupons(coupons, updatedCoupons, isCartPage));

    const couponRemoved = coupons.find(c => c.couponCode === couponCode);

    trackCouponEvent({
      event: 'coupon_remove',
      location: lastestCouponLocation,
      coupon: couponRemoved,
    });
  };

  const getCouponWalletContent = async ({
    couponCode,
    shouldGenerateQrCode,
    platform,
  }: {
    couponCode?: string;
    shouldGenerateQrCode?: boolean;
    platform?: 'ios' | 'android';
  }): Promise<CouponWalletContent> => {
    if (!couponCode) return { errorCode: 404 };
    try {
      let walletUrls: CouponWalletContent = { ...selectedCoupon?.digitalCard };

      if (!walletUrls.appleLink && !walletUrls?.googleLink) {
        walletUrls = await queryClient.fetchQuery(['getCouponWalletContent', couponCode], async ({ signal }) => {
          const response = await SubscriptionsClient.getAddToWalletLinks({ couponCode, signal });
          if (response.error || response.statusCode !== 200) {
            return { errorCode: response.statusCode };
          }
          return response.data;
        });
      }
      if (!walletUrls?.appleLink && !walletUrls?.googleLink) {
        return { errorCode: walletUrls?.errorCode };
      }

      if (selectedCoupon) {
        const updatedCoupon: Coupon = {
          ...selectedCoupon,
          digitalCard: { appleLink: walletUrls.appleLink, googleLink: walletUrls.googleLink },
        };
        const updatedCoupons = [
          ...coupons.filter(coupon => coupon.couponCode !== selectedCoupon.couponCode),
          updatedCoupon,
        ];
        setCoupons(updatedCoupons);
      }

      const targetUrl = platform === 'ios' ? walletUrls?.appleLink : walletUrls?.googleLink;

      const qrCode =
        shouldGenerateQrCode && targetUrl
          ? await queryClient.fetchQuery(
              ['generateAddToWalletQRCode', targetUrl],
              async () => {
                return await generateAddToWalletQRCode(targetUrl);
              },
              { cacheTime: 30 * 60 * 1000, staleTime: 30 * 60 * 1000 }
            )
          : undefined;
      return {
        appleLink: walletUrls?.appleLink,
        googleLink: walletUrls?.googleLink,
        qrCode,
      };
    } catch (error) {
      const statusCode = (error as { statusCode?: number })?.statusCode;
      console.error('Error fetching add to wallet content for coupon code:', couponCode, error);
      return { errorCode: statusCode };
    }
  };

  const viewedCouponsCount = coupons.reduce((count, coupon) => {
    return !coupon.isViewed ? count + 1 : count;
  }, 0);

  const shouldShowCoupons = () => {
    switch (location) {
      case 'cart':
        return isCouponSurfacingEnabledForCart;
      case 'slideout':
        return isCouponSurfacingEnabledForAttachSlideout;
      default:
        return isCouponSurfacingEnabled;
    }
  };

  const { progress, isProductExcluded } = useSpendAndGetProgress(cart?.items, couponOffering, exclusionMap, product);

  useEffect(() => {
    const shouldReopenBanner =
      couponOffering?.minimumSpend &&
      progress < couponOffering.minimumSpend &&
      !couponOffering.success &&
      !shouldShowSpendAndGetBanner;

    if (!shouldReopenBanner) return;

    const shouldShowBannerFlag = getLocal(LocalStorageKeys.ShouldShowSpendAndGetBanner, true);

    if (shouldShowBannerFlag) {
      setShouldShowSpendAndGetBanner(true);
    }
  }, [
    cart?.item_count,
    progress,
    couponOffering?.minimumSpend,
    couponOffering?.success,
    shouldShowSpendAndGetBanner,
    setShouldShowSpendAndGetBanner,
  ]);

  return {
    isCouponSurfacingEnabled: shouldShowCoupons(),
    coupons,
    couponsWithExclusions,
    isLoading,
    isApplyingCoupon: false,
    isRemovingCoupon: false,
    isModalOpen,
    selectedCoupon,
    newCouponCount: viewedCouponsCount,
    isFetched,
    isActive,
    shouldShowSpendAndGetBanner,
    currentSpend: progress,
    isProductExcluded,
    couponOffering,
    handleToggleIsModalOpen,
    handleSelectCoupon,
    handleApplyCoupon,
    handleRemoveCoupon,
    handleDeselectCoupon,
    handleViewCoupons,
    handleUpdateShowSpendAndGetBanner,
    getCouponWalletContent,
  };
};
